import {
  Box,
  Button,
  Checkbox,
  Flex,
  FormControl,
  FormHelperText,
  FormLabel,
  H2,
  Spinner,
  TextInput,
  Textarea,
} from '@ouster/webviz-react-components';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import useChakraHook from '../../hooks/useChakraHook';
import { OUSTER_STUDIO_DOWNLOAD_URL } from '../../utils/constants';
import {
  defaultPolicyMessageForForceUpdate,
  fetchOsdCompatibilityPolicy,
  formatPolicyTimestamp,
  isStockPolicyMessage,
  isValidHttpUrl,
  isValidSemver,
  OSD_COMPATIBILITY_POLICY_QUERY_KEY,
  sanitizePolicyMessageHtml,
  saveOsdCompatibilityPolicy,
  toAdminFormPolicy,
  type OsdCompatibilityPolicy,
} from './osdCompatibilityPolicy';

type FormState = {
  min_supported_version: string;
  force_update: boolean;
  message: string;
  download_url: string;
};

const toFormState = (policy: OsdCompatibilityPolicy): FormState => {
  const formPolicy = toAdminFormPolicy(policy);
  return {
    min_supported_version: formPolicy.min_supported_version,
    force_update: formPolicy.force_update,
    message: formPolicy.message,
    download_url: formPolicy.download_url,
  };
};

export default function DevToolOsdMgmt() {
  const { showToast } = useChakraHook();
  const queryClient = useQueryClient();

  const {
    data: policy,
    isPending,
    isFetching,
    error,
    isError,
  } = useQuery({
    queryKey: OSD_COMPATIBILITY_POLICY_QUERY_KEY,
    queryFn: fetchOsdCompatibilityPolicy,
  });

  // null means "mirror loaded policy"; non-null is local edits.
  // Form is not interactive until policy is defined, so draft cannot seed from
  // defaults and clobber the arriving row.
  const [draft, setDraft] = useState<FormState | null>(null);

  const baseline = policy ? toFormState(policy) : null;
  const form = draft ?? baseline;

  useEffect(() => {
    if (error) {
      showToast({
        title: 'Failed to load OSD compatibility policy',
        description: `${error}`,
        status: 'error',
      });
    }
  }, [error, showToast]);

  const previewHtml = form ? sanitizePolicyMessageHtml(form.message) : '';

  const minVersionError =
    form &&
    form.min_supported_version.trim().length > 0 &&
    !isValidSemver(form.min_supported_version)
      ? 'Enter a valid semver value (for example 2.7.0 or 2.7.0-dev.4).'
      : null;

  const downloadUrlError =
    form &&
    form.download_url.trim().length > 0 &&
    !isValidHttpUrl(form.download_url)
      ? 'Enter an absolute http(s) URL, or leave blank to use the corporate default.'
      : null;

  const saveMutation = useMutation({
    mutationFn: saveOsdCompatibilityPolicy,
    onSuccess: (saved) => {
      queryClient.setQueryData(OSD_COMPATIBILITY_POLICY_QUERY_KEY, saved);
      setDraft(null);
      showToast({
        title: 'OSD compatibility policy saved',
        description:
          'Desktop clients will read this on next launch when online.',
        status: 'success',
      });
    },
    onError: (saveError: unknown) => {
      showToast({
        title: 'Failed to save OSD compatibility policy',
        description: `${saveError}`,
        status: 'error',
      });
    },
  });

  const updateDraft = (patch: Partial<FormState>) => {
    if (!baseline) {
      return;
    }
    setDraft((current) => ({ ...(current ?? baseline), ...patch }));
  };

  const handleForceUpdateChange = (force_update: boolean) => {
    if (!baseline) {
      return;
    }
    setDraft((current) => {
      const next = { ...(current ?? baseline), force_update };
      // Swap stock copy when the admin has not customized the message.
      if (isStockPolicyMessage(next.message)) {
        next.message = defaultPolicyMessageForForceUpdate(force_update);
      }
      return next;
    });
  };

  const handleUseDefaultMessage = () => {
    if (!form) {
      return;
    }
    updateDraft({
      message: defaultPolicyMessageForForceUpdate(form.force_update),
    });
  };

  const handleResetToLoaded = () => {
    setDraft(null);
  };

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!form || minVersionError || downloadUrlError) {
      return;
    }
    if (!isValidSemver(form.min_supported_version)) {
      return;
    }

    saveMutation.mutate({
      min_supported_version: form.min_supported_version.trim(),
      force_update: form.force_update,
      message: form.message,
      download_url: form.download_url.trim() || OUSTER_STUDIO_DOWNLOAD_URL,
    });
  };

  const isSaveDisabled =
    !form ||
    isFetching ||
    saveMutation.isPending ||
    Boolean(minVersionError) ||
    Boolean(downloadUrlError) ||
    !isValidSemver(form.min_supported_version);

  return (
    <Box p="25px 36px 24px" data-testid="osd-mgmt-page" mb={8}>
      <Box mb={4} maxW="80rem">
        {/* Match peer admin pages: H2 medium @ 30 */}
        <H2 fontWeight="medium" fontSize={30}>
          OSD Management
        </H2>
        <Box mt={3} color="gray.300" lineHeight={1.6} fontSize={16}>
          Configure the Client Compatibility Policy for Ouster Studio Desktop.
          Below min + force update hard-blocks; below min without force shows a
          dismissible modal. Offline desktops fail open.
        </Box>
      </Box>

      {isPending && !policy ? (
        <Flex
          alignItems="center"
          justifyContent="center"
          minH="12rem"
          data-testid="osd-mgmt-loading"
        >
          <Spinner size="xl" />
        </Flex>
      ) : null}

      {isError && !policy ? (
        <Box color="red.300" fontSize={16} data-testid="osd-mgmt-load-error">
          Could not load the compatibility policy. Refresh the page to try
          again.
        </Box>
      ) : null}

      {form ? (
        <Box
          as="form"
          // eslint-disable-next-line @typescript-eslint/no-misused-promises -- form submit handler
          onSubmit={handleSubmit}
          maxW="96rem"
          border="1px solid"
          borderColor="gray.700"
          borderRadius="lg"
          backgroundColor="gray.800"
          overflow="hidden"
          data-testid="osd-mgmt-form"
        >
          <Box
            display="grid"
            gridTemplateColumns={{
              base: '1fr',
              lg: 'minmax(0, 1fr) minmax(0, 1.15fr)',
            }}
            columnGap={{ base: 0, lg: 10 }}
            rowGap={8}
            alignItems="start"
            p={{ base: 5, md: 8 }}
          >
            {/* Left: version gate + download */}
            <Box
              display="grid"
              rowGap={8}
              minW={0}
              placeContent="space-between"
              h="full"
            >
              <Box>
                <FormControl
                  paddingInline={0}
                  isInvalid={Boolean(minVersionError)}
                  display="grid"
                  rowGap={2}
                >
                  <FormLabel
                    color="gray.100"
                    fontWeight="semibold"
                    mb={5}
                    fontSize={16}
                  >
                    Min supported version
                  </FormLabel>
                  <TextInput
                    value={form.min_supported_version}
                    onChange={(event) =>
                      updateDraft({ min_supported_version: event.target.value })
                    }
                    placeholder="2.7.0"
                    isRequired
                    data-testid="osd-mgmt-min-version"
                  />
                  {/* Peer admin meta/helper copy uses 14px (e.g. Copied Recordings). */}
                  <FormHelperText
                    minH="auto"
                    m={0}
                    fontSize={14}
                    lineHeight={1.6}
                  >
                    {minVersionError ||
                      'Inclusive lower bound. Desktop compares with full semver, including prerelease tags.'}
                  </FormHelperText>
                </FormControl>

                <Checkbox
                  isChecked={form.force_update}
                  onChange={(event) =>
                    handleForceUpdateChange(event.target.checked)
                  }
                  label="Force update (hard block when below min supported version)"
                  data-testid="osd-mgmt-force-update"
                />
              </Box>

              <FormControl
                paddingInline={0}
                isInvalid={Boolean(downloadUrlError)}
                display="grid"
                rowGap={2}
              >
                <FormLabel
                  color="gray.100"
                  fontWeight="semibold"
                  mb={2}
                  fontSize={16}
                >
                  Download URL
                </FormLabel>
                <TextInput
                  value={form.download_url}
                  onChange={(event) =>
                    updateDraft({ download_url: event.target.value })
                  }
                  placeholder={OUSTER_STUDIO_DOWNLOAD_URL}
                  data-testid="osd-mgmt-download-url"
                />
                <FormHelperText
                  minH="auto"
                  m={0}
                  fontSize={14}
                  lineHeight={1.6}
                >
                  {downloadUrlError ||
                    `Corporate default: ${OUSTER_STUDIO_DOWNLOAD_URL}. Empty values fall back to that default on save.`}
                </FormHelperText>
              </FormControl>
            </Box>

            {/* Right: message editor + live preview */}
            <Box display="grid" rowGap={5} minW={0}>
              <FormControl paddingInline={0} display="grid" rowGap={3}>
                <Flex
                  placeContent="space-between"
                  alignItems="center"
                  gap="1rem"
                  flexWrap="wrap"
                >
                  <FormLabel
                    color="gray.100"
                    fontWeight="semibold"
                    mb={0}
                    fontSize={16}
                  >
                    Policy message (HTML)
                  </FormLabel>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={handleUseDefaultMessage}
                    data-testid="osd-mgmt-use-default-message"
                  >
                    Use default for{' '}
                    {form.force_update ? 'force update' : 'soft warning'}
                  </Button>
                </Flex>
                <Textarea
                  value={form.message}
                  onChange={(event) =>
                    updateDraft({ message: event.target.value })
                  }
                  minH="12rem"
                  lineHeight={1.6}
                  placeholder={defaultPolicyMessageForForceUpdate(
                    form.force_update
                  )}
                  data-testid="osd-mgmt-message"
                />
                <FormHelperText
                  minH="auto"
                  m={0}
                  mb={4}
                  fontSize={14}
                  lineHeight={1.6}
                >
                  Defaults differ for hard block vs soft warning. Keep{' '}
                  {'{current_version}'} and {'{latest_version}'} as
                  placeholders; desktop fills them on launch. Allowed tags: a,
                  p, br, strong, em, ul, ol, li. Anchors must use http(s) hrefs.
                  Server sanitizes on save; preview is best-effort (placeholders
                  shown literally).
                </FormHelperText>
              </FormControl>

              <Box
                border="1px solid"
                borderColor="gray.600"
                borderRadius="md"
                p={{ base: 4, md: 5 }}
                backgroundColor="blackAlpha.400"
                data-testid="osd-mgmt-message-preview"
              >
                <Box
                  fontWeight="medium"
                  color="gray.300"
                  mb={6}
                  textTransform="uppercase"
                >
                  Message preview
                </Box>
                {previewHtml ? (
                  <Box
                    className="osd-mgmt-message-preview-body"
                    color="gray.100"
                    fontSize={16}
                    lineHeight={1.6}
                    dangerouslySetInnerHTML={{ __html: previewHtml }}
                    sx={{
                      a: {
                        color: 'content-action',
                        textDecoration: 'underline',
                      },
                      p: { mb: 3, _last: { mb: 0 }, lineHeight: 1.7 },
                      'ul, ol': { pl: 5, mb: 3 },
                      li: { mb: 1.5 },
                    }}
                  />
                ) : (
                  <Box color="gray.400" fontSize={16} lineHeight={1.6}>
                    No message
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          <Box
            mt={10}
            p={{ base: 5, md: 7 }}
            borderTop="1px solid"
            borderColor="gray.700"
            backgroundColor="blackAlpha.400"
          >
            <Flex
              gap={5}
              justifyContent="space-between"
              alignItems="flex-end"
              flexWrap="wrap"
            >
              <Box
                display="grid"
                rowGap={2}
                color="gray.300"
                fontSize={16}
                lineHeight={1.6}
                data-testid="osd-mgmt-audit"
              >
                <Box>
                  Last updated:{' '}
                  <Box as="span" fontWeight="medium" color="gray.100">
                    {formatPolicyTimestamp(policy?.updated_at ?? null)}
                  </Box>
                </Box>
                <Box>
                  Updated by:{' '}
                  <Box as="span" fontWeight="medium" color="gray.100">
                    {policy?.updated_by?.trim() ? policy.updated_by : '—'}
                  </Box>
                </Box>
              </Box>

              <Flex gap={3} flexWrap="wrap">
                <Button
                  type="button"
                  variant="outline"
                  subtle
                  minW="5.5rem"
                  onClick={handleResetToLoaded}
                  isDisabled={
                    !policy || saveMutation.isPending || draft === null
                  }
                  data-testid="osd-mgmt-reset"
                >
                  Reset
                </Button>
                <Button
                  type="submit"
                  variant="solid"
                  minW="5.5rem"
                  isDisabled={isSaveDisabled}
                  isLoading={saveMutation.isPending}
                  data-testid="osd-mgmt-save"
                >
                  Save
                </Button>
              </Flex>
            </Flex>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
}
